import { useCallback, useEffect, useRef, useState } from 'react';
import { currentLocale } from '../i18n/i18n';

// Autosave for free-text fields: local value follows typing, persists after a short idle
// delay, on blur (flush) and on unmount. The inspector never has to press "save" for a note.
export function useAutosaveText(
  remote: string,
  save: (value: string) => Promise<unknown>,
  delayMs: number = 600
): { value: string; setValue: (v: string) => void; flush: () => Promise<void>; savedAt: number | null } {
  const [value, setValue] = useState(remote);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const latest = useRef(value);
  latest.current = value;
  const remoteRef = useRef(remote);

  useEffect(() => {
    if (remote !== remoteRef.current) {
      remoteRef.current = remote;
      setValue(remote);
    }
  }, [remote]);

  const flush = useCallback(async () => {
    if (latest.current === remoteRef.current) return;
    const pending = latest.current;
    remoteRef.current = pending;
    await save(pending);
    setSavedAt(Date.now());
  }, [save]);

  useEffect(() => {
    if (value === remoteRef.current) return;
    const timer = setTimeout(() => {
      void flush();
    }, delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs, flush]);

  useEffect(
    () => () => {
      void flush();
    },
    [flush]
  );

  return { value, setValue, flush, savedAt };
}

export function formatClock(ts: number): string {
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

// Dates follow the App Language (zh-CN → 2026/9/3, en-US → 9/3/2026).
export function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString(currentLocale());
}

// Real connectivity (navigator.onLine + events). Presentation only — the shell shows an
// offline / local status chip from it; nothing here gates any field action (Authority §13).
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}
